import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Re-renders a "Complete the look" section after any cart change (quick add on
 * its own cards, the product form, quantity or remove in the cart), so it never
 * suggests something that is now in the cart. Uses the same event and section
 * renderer as the cart items component.
 */
class CompleteTheLook extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  #onCartLinesUpdate = (event) => {
    const { sectionId } = this.dataset;
    if (!sectionId) return;

    (event.promise ?? Promise.resolve())
      .then(() => sectionRenderer.renderSection(sectionId, { cache: false, injectStylesheet: true }))
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[complete-the-look]', error);
      });
  };
}

if (!customElements.get('complete-the-look')) {
  customElements.define('complete-the-look', CompleteTheLook);
}

/*
 * Cart drawer strip (snippets/complete-the-look-drawer.liquid). Its buttons are
 * plain theme product forms; the drawer re-renders after each add.
 *
 * - One add per tap: a second tap while the first add is in flight is
 *   stopped on window in the capture phase, which runs before the theme's own
 *   document-level submit handler (assets/component.js).
 * - Focus: the re-render removes the tapped button and drops focus to <body>;
 *   put it back in the drawer (strip heading, else the close button).
 * - Errors: if an add fails (e.g. the size just sold out), say so in the
 *   strip's status line, which the drawer re-render leaves alone.
 */
const STRIP = '.complete-look';
const RE_ENABLE_AFTER_MS = 8000;
const FOCUS_CHECKS_MS = [100, 600, 1500];
const ERROR_SHOWN_MS = 8000;

window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest(STRIP)) return;

    const button = /** @type {HTMLButtonElement | null} */ (
      event.submitter ?? form.querySelector('[type="submit"]')
    );
    if (button?.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    button?.setAttribute('aria-disabled', 'true');
    setTimeout(() => button?.removeAttribute('aria-disabled'), RE_ENABLE_AFTER_MS);
  },
  { capture: true }
);

/** @param {Event} event */
const fromStrip = (event) => event.target instanceof Element && Boolean(event.target.closest(STRIP));

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (!fromStrip(event)) return;
  const { promise } = /** @type {Event & { promise?: Promise<any> }} */ (event);

  (promise ?? Promise.resolve())
    .catch(() => {})
    .then(() => {
      // The drawer re-render can land after a further fetch, so check a few times.
      for (const delay of FOCUS_CHECKS_MS) {
        setTimeout(() => {
          const drawer = document.querySelector('#cart-drawer dialog[open]');
          if (!drawer || drawer.contains(document.activeElement)) return;

          const target = /** @type {HTMLElement | null} */ (
            drawer.querySelector(`${STRIP} .complete-look__heading`) ??
              drawer.querySelector('.theme-drawer__close-button')
          );
          target?.focus();
        }, delay);
      }
    });
});

document.addEventListener(StandardEvents.cartError, (event) => {
  if (!fromStrip(event)) return;
  const status = document.querySelector(`#cart-drawer ${STRIP} .complete-look__status`);
  if (!status) return;

  const { error } = /** @type {Event & { error?: string }} */ (event);
  status.textContent = error || status.getAttribute('data-default-error') || '';
  setTimeout(() => {
    status.textContent = '';
  }, ERROR_SHOWN_MS);
});
